<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import axios from '@axios'
import { numberWithCommas } from '@/plugins/misc'
import { useDesignSystem } from '@/composables/useDesignSystem'

const props = defineProps({
  fans: {
    type: Array,
    required: true,
  },
  restaurants: {
    type: Array,
    required: true,
  },

  // The restaurant pinned by the Fans-page filter, if any. When set, it is the
  // forced sender and the in-dialog restaurant picker is hidden.
  restaurantId: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['close', 'sent'])

const { dialogWidth } = useDesignSystem()
const width = dialogWidth('sm')

const MAX_TEXT = 2000
const MAX_ATTACHMENTS = 10

// ─── State ──────────────────────────────────────────────────────────────────
// Parent mounts this component only while the dialog should be open; the ref
// just satisfies VDialog's model and lets a backdrop click route to close().
const dialogOpen = ref(true)
const selectedRestaurantId = ref(props.restaurantId || null)
const messageText = ref('')
const sending = ref(false)
const sendError = ref(null)
const recipientExpanded = ref(false)

// Attachments
const attachments = ref([])
const uploading = ref(false)
const uploadError = ref(null)
const fileInput = ref(null)

// Link preview
const linkPreview = ref(null)
const linkPreviewLoading = ref(false)
const dismissedUrls = ref(new Set())
let linkPreviewTimer = null
let lastPreviewedUrl = null

// ─── Restaurant resolution ───────────────────────────────────────────────────

/** { [restaurantId]: Set<fanId> } — which fans have favorited each restaurant. */
const fansByRestaurant = computed(() => {
  const map = {}
  for (const r of props.restaurants) map[r._id] = new Set()
  for (const fan of props.fans) {
    for (const rid of fan.favoritedRestaurantIds || []) {
      if (map[rid]) map[rid].add(fan._id)
    }
  }

  return map
})

/** Restaurants every selected fan has favorited — the only clean senders. */
const eligibleRestaurants = computed(() =>
  props.restaurants.filter(r => {
    const fanSet = fansByRestaurant.value[r._id]

    return fanSet && fanSet.size === props.fans.length
  }),
)

/** Fans who favorited none of the in-scope restaurants — skipped on send. */
const skippedFans = computed(() => {
  const eligibleFanIds = new Set()
  for (const r of props.restaurants) {
    const fanSet = fansByRestaurant.value[r._id]
    if (fanSet) fanSet.forEach(fid => eligibleFanIds.add(fid))
  }

  return props.fans.filter(f => !eligibleFanIds.has(f._id))
})

// A filter-pinned restaurant always wins; otherwise auto-pick the sole eligible
// restaurant. Only a genuine multi-choice leaves the picker visible.
watch(
  () => [props.restaurantId, eligibleRestaurants.value],
  () => {
    if (props.restaurantId) {
      selectedRestaurantId.value = props.restaurantId
    } else if (eligibleRestaurants.value.length === 1) {
      selectedRestaurantId.value = eligibleRestaurants.value[0]._id
    }
  },
  { immediate: true },
)

const showRestaurantPicker = computed(
  () => !props.restaurantId && eligibleRestaurants.value.length > 1,
)

const senderRestaurant = computed(() => {
  const id = selectedRestaurantId.value
  if (!id) return null

  return props.restaurants.find(r => String(r._id) === String(id)) || null
})

const recipientCount = computed(() => {
  if (!selectedRestaurantId.value) {
    return props.fans.length - skippedFans.value.length
  }
  const fanSet = fansByRestaurant.value[selectedRestaurantId.value] || new Set()

  return props.fans.filter(f => fanSet.has(f._id)).length
})

// ─── Derived ─────────────────────────────────────────────────────────────────
const charCount = computed(() => messageText.value.length)
const charOver = computed(() => charCount.value > MAX_TEXT)

const canSend = computed(() =>
  Boolean(selectedRestaurantId.value)
  && (messageText.value.trim().length > 0 || attachments.value.length > 0)
  && !charOver.value
  && recipientCount.value > 0
  && !sending.value
  && !uploading.value,
)

function fanName(fan) {
  return `${fan.firstName || ''} ${fan.lastName || ''}`.trim() || 'Member'
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`

  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// ─── Attachments ─────────────────────────────────────────────────────────────
const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf'

function openFilePicker() {
  uploadError.value = null
  if (fileInput.value) fileInput.value.click()
}

async function onFilesSelected(event) {
  const picked = Array.from(event.target.files || [])
  event.target.value = '' // allow re-picking the same file
  if (picked.length === 0) return

  const room = MAX_ATTACHMENTS - attachments.value.length
  if (room <= 0) {
    uploadError.value = `You can attach at most ${MAX_ATTACHMENTS} files`

    return
  }

  uploadError.value = null
  uploading.value = true
  try {
    const results = await Promise.all(picked.slice(0, room).map(uploadOne))
    const ok = results.filter(Boolean)
    if (ok.length) attachments.value = [...attachments.value, ...ok]
  } finally {
    uploading.value = false
  }
}

async function uploadOne(file) {
  try {
    const form = new FormData()
    form.append('file', file)
    const { data } = await axios.post('/messaging/restaurantAdmin/attachments', form)

    return data && data.attachment ? data.attachment : null
  } catch (err) {
    uploadError.value = err?.response?.data?.message || `Could not upload ${file.name}`

    return null
  }
}

function removeAttachment(index) {
  attachments.value = attachments.value.filter((_, i) => i !== index)
}

// ─── Link preview ────────────────────────────────────────────────────────────
const URL_RE = /\bhttps?:\/\/[^\s<>"']+/i

watch(messageText, text => {
  clearTimeout(linkPreviewTimer)
  const match = text.match(URL_RE)
  const url = match ? match[0].replace(/[.,;:!?)]+$/, '') : null

  if (!url) {
    linkPreview.value = null
    lastPreviewedUrl = null

    return
  }
  if (url === lastPreviewedUrl || dismissedUrls.value.has(url)) return

  linkPreviewTimer = setTimeout(() => fetchPreview(url), 600)
})

async function fetchPreview(url) {
  lastPreviewedUrl = url
  linkPreviewLoading.value = true
  try {
    const { data } = await axios.post('/messaging/restaurantAdmin/link-preview', { url })
    linkPreview.value = data && data.preview ? data.preview : null
  } catch {
    linkPreview.value = null
  } finally {
    linkPreviewLoading.value = false
  }
}

function dismissPreview() {
  if (lastPreviewedUrl) dismissedUrls.value.add(lastPreviewedUrl)
  linkPreview.value = null
}

// ─── Send ────────────────────────────────────────────────────────────────────
async function send() {
  if (!canSend.value) return
  sendError.value = null
  sending.value = true
  try {
    const fanSet = fansByRestaurant.value[selectedRestaurantId.value] || new Set()
    const memberUserIds = props.fans
      .filter(f => fanSet.has(f._id))
      .map(f => f._id)

    const { data } = await axios.post('/messaging/restaurantAdmin/bulk', {
      restaurantId: selectedRestaurantId.value,
      memberUserIds,
      text: messageText.value.trim(),
      attachments: attachments.value,
      linkPreview: linkPreview.value || null,
    })

    emit('sent', { jobCount: data?.jobCount ?? memberUserIds.length })
  } catch (err) {
    sendError.value = err?.response?.data?.message || 'Failed to send. Please try again.'
    sending.value = false
  }
}

function close() {
  emit('close')
}

onUnmounted(() => {
  clearTimeout(linkPreviewTimer)
})
</script>

<template>
  <VDialog
    :model-value="dialogOpen"
    :width="width"
    :max-width="560"
    persistent
    scrollable
    @update:model-value="close"
  >
    <VCard class="bmd">
      <!-- Header -->
      <div class="bmd__header">
        <div class="bmd__header-text">
          <div class="bmd__title">
            New message
          </div>
          <div class="bmd__subtitle">
            {{ numberWithCommas(recipientCount) }} {{ recipientCount === 1 ? 'recipient' : 'recipients' }}
          </div>
          <div class="bmd__subtitle">
            Message will be sent individually to fans
          </div>
        </div>
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          @click="close"
        />
      </div>

      <VDivider />

      <div class="bmd__body">
        <!-- Recipients -->
        <div class="bmd__row">
          <span class="bmd__row-label">To</span>
          <div class="bmd__row-value">
            <button
              type="button"
              class="bmd__recipients-toggle"
              @click="recipientExpanded = !recipientExpanded"
            >
              <span>{{ numberWithCommas(fans.length) }} {{ fans.length === 1 ? 'fan' : 'fans' }}</span>
              <VIcon size="16">
                {{ recipientExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </VIcon>
            </button>
            <VExpandTransition>
              <div
                v-if="recipientExpanded"
                class="bmd__chip-list"
              >
                <span
                  v-for="fan in fans"
                  :key="fan._id"
                  class="bmd__chip"
                >
                  {{ fanName(fan) }}
                </span>
              </div>
            </VExpandTransition>
          </div>
        </div>

        <!-- Sender restaurant -->
        <div class="bmd__row">
          <span class="bmd__row-label">From</span>
          <div class="bmd__row-value">
            <VSelect
              v-if="showRestaurantPicker"
              v-model="selectedRestaurantId"
              :items="eligibleRestaurants"
              item-title="name"
              item-value="_id"
              variant="outlined"
              density="compact"
              hide-details
              placeholder="Choose a restaurant"
              style="font-size: 16px;"
            />
            <span
              v-else-if="senderRestaurant"
              class="bmd__sender-name"
            >{{ senderRestaurant.name }}</span>
            <span
              v-else
              class="bmd__sender-empty"
            >No eligible restaurant</span>
          </div>
        </div>

        <!-- Skipped fans -->
        <VAlert
          v-if="skippedFans.length > 0"
          type="warning"
          variant="tonal"
          density="compact"
          class="bmd__alert"
        >
          {{ skippedFans.length }} {{ skippedFans.length === 1 ? 'recipient hasn’t' : 'recipients haven’t' }}
          favorited your restaurant yet and won’t receive this message.
        </VAlert>

        <!-- Message body -->
        <VTextarea
          v-model="messageText"
          placeholder="Write your message…"
          variant="plain"
          rows="5"
          auto-grow
          hide-details
          class="bmd__textarea"
          style="font-size: 16px;"
        />

        <!-- Link preview -->
        <div
          v-if="linkPreviewLoading"
          class="bmd__preview bmd__preview--loading"
        >
          <VProgressCircular
            indeterminate
            size="18"
            width="2"
            color="primary-dark"
          />
          <span>Loading link preview…</span>
        </div>
        <div
          v-else-if="linkPreview"
          class="bmd__preview"
        >
          <VImg
            v-if="linkPreview.imageUrl"
            :src="linkPreview.imageUrl"
            :width="84"
            :height="84"
            cover
            class="bmd__preview-img"
          />
          <div class="bmd__preview-text">
            <div
              v-if="linkPreview.siteName"
              class="bmd__preview-site"
            >
              {{ linkPreview.siteName }}
            </div>
            <div class="bmd__preview-title">
              {{ linkPreview.title || linkPreview.url }}
            </div>
            <div
              v-if="linkPreview.description"
              class="bmd__preview-desc"
            >
              {{ linkPreview.description }}
            </div>
          </div>
          <VBtn
            icon="mdi-close"
            variant="text"
            size="x-small"
            class="bmd__preview-dismiss"
            aria-label="Remove link preview"
            @click="dismissPreview"
          />
        </div>

        <!-- Attachments -->
        <div
          v-if="attachments.length > 0"
          class="bmd__attachments"
        >
          <div
            v-for="(att, idx) in attachments"
            :key="att.url"
            class="bmd__attachment"
            :class="{ 'bmd__attachment--file': att.kind !== 'image' }"
          >
            <VImg
              v-if="att.kind === 'image'"
              :src="att.url"
              :width="72"
              :height="72"
              cover
              class="bmd__attachment-thumb"
            />
            <template v-else>
              <VIcon
                size="22"
                color="error"
              >
                mdi-file-pdf-box
              </VIcon>
              <div class="bmd__attachment-meta">
                <div class="bmd__attachment-name">
                  {{ att.name }}
                </div>
                <div class="bmd__attachment-size">
                  {{ formatBytes(att.size) }}
                </div>
              </div>
            </template>
            <button
              type="button"
              class="bmd__attachment-remove"
              aria-label="Remove attachment"
              @click="removeAttachment(idx)"
            >
              <VIcon size="14">
                mdi-close
              </VIcon>
            </button>
          </div>
        </div>

        <!-- Errors -->
        <VAlert
          v-if="uploadError"
          type="error"
          variant="tonal"
          density="compact"
          class="bmd__alert"
        >
          {{ uploadError }}
        </VAlert>
        <VAlert
          v-if="sendError"
          type="error"
          variant="tonal"
          density="compact"
          class="bmd__alert"
        >
          {{ sendError }}
        </VAlert>
      </div>

      <VDivider />

      <!-- Footer -->
      <div class="bmd__footer">
        <div class="bmd__tools">
          <!-- Attachments temporarily disabled; wrapper keeps the tooltip working on the disabled button -->
          <div class="bmd__attach-disabled">
            <VBtn
              icon
              variant="text"
              size="small"
              disabled
              aria-label="Attach image or PDF — coming soon"
              @click="openFilePicker"
            >
              <VIcon>mdi-paperclip</VIcon>
            </VBtn>
            <VTooltip
              activator="parent"
              location="top"
            >
              Coming soon
            </VTooltip>
          </div>
          <span
            class="bmd__counter"
            :class="[{ 'bmd__counter--over': charOver }]"
          >{{ numberWithCommas(charCount) }}/{{ numberWithCommas(MAX_TEXT) }}</span>
        </div>
        <div class="bmd__footer-actions">
          <VBtn
            variant="text"
            :disabled="sending"
            @click="close"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary-dark"
            variant="flat"
            :loading="sending"
            :disabled="!canSend"
            @click="send"
          >
            Send
          </VBtn>
        </div>
      </div>

      <input
        ref="fileInput"
        type="file"
        :accept="ACCEPT"
        multiple
        class="bmd__file-input"
        @change="onFilesSelected"
      >
    </VCard>
  </VDialog>
</template>

<style scoped>
.bmd {
  border-radius: var(--ds-radius-lg, 14px);
  overflow: hidden;
}

/* ─── Header ─────────────────────────────────────────────────────────────── */
.bmd__header {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-4, 16px) var(--ds-space-4, 16px) var(--ds-space-3, 12px) var(--ds-space-5, 20px);
}

.bmd__header-text {
  flex: 1;
  min-width: 0;
}

.bmd__title {
  font-size: 18px;
  font-weight: 600;
  color: var(--ds-text-strong, #1a1a1a);
  line-height: 1.3;
}

.bmd__subtitle {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-top: 2px;
}

/* ─── Body ───────────────────────────────────────────────────────────────── */
.bmd__body {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-4, 16px) var(--ds-space-5, 20px);
}

.bmd__row {
  display: flex;
  align-items: baseline;
  gap: var(--ds-space-3, 12px);
}

.bmd__row-label {
  flex: none;
  width: 42px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.bmd__row-value {
  flex: 1;
  min-width: 0;
}

.bmd__sender-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-text-strong, #1a1a1a);
}

.bmd__sender-empty {
  font-size: 14px;
  color: rgb(var(--v-theme-error, 211, 47, 47));
}

.bmd__recipients-toggle {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px 2px 0;
  background: transparent;
  border: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-text-strong, #1a1a1a);
  cursor: pointer;
}

.bmd__chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 132px;
  overflow-y: auto;
  padding-top: var(--ds-space-2, 8px);
}

.bmd__chip {
  padding: 3px 10px;
  background: var(--ds-brand-primary-light, #e9f4f3);
  border-radius: 999px;
  font-size: 12px;
  color: var(--ds-brand-primary-darker, #0FA091);
}

/* ─── Message body ───────────────────────────────────────────────────────── */
.bmd__textarea {
  background: #f5f6f7;
  border-radius: var(--ds-radius-md, 10px);
  padding: 4px 12px;
}

/* ─── Link preview ───────────────────────────────────────────────────────── */
.bmd__preview {
  position: relative;
  display: flex;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-2, 8px);
  background: #f5f6f7;
  border: 1px solid var(--ds-border-light, #e5e5e5);
  border-radius: var(--ds-radius-md, 10px);
}

.bmd__preview--loading {
  align-items: center;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  padding: var(--ds-space-3, 12px);
}

.bmd__preview-img {
  flex: none;
  border-radius: var(--ds-radius-sm, 6px);
}

.bmd__preview-text {
  min-width: 0;
  padding-right: 22px;
}

.bmd__preview-site {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.bmd__preview-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-strong, #1a1a1a);
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.bmd__preview-desc {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.bmd__preview-dismiss {
  position: absolute;
  top: 2px;
  right: 2px;
}

/* ─── Attachments ────────────────────────────────────────────────────────── */
.bmd__attachments {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-space-2, 8px);
}

.bmd__attachment {
  position: relative;
  border-radius: var(--ds-radius-sm, 8px);
}

.bmd__attachment-thumb {
  border-radius: var(--ds-radius-sm, 8px);
  border: 1px solid var(--ds-border-light, #e5e5e5);
}

.bmd__attachment--file {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 220px;
  padding: 8px 28px 8px 10px;
  background: #f5f6f7;
  border: 1px solid var(--ds-border-light, #e5e5e5);
}

.bmd__attachment-meta {
  min-width: 0;
}

.bmd__attachment-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ds-text-strong, #1a1a1a);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bmd__attachment-size {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.bmd__attachment-remove {
  position: absolute;
  top: -7px;
  right: -7px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #1a1a1a;
  color: #fff;
  border: 2px solid #fff;
  border-radius: 50%;
  cursor: pointer;
}

.bmd__alert {
  font-size: 13px;
}

/* ─── Footer ─────────────────────────────────────────────────────────────── */
.bmd__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  padding: var(--ds-space-3, 12px) var(--ds-space-4, 16px);
}

.bmd__tools {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2, 8px);
}

.bmd__attach-disabled {
  display: inline-flex;
}

.bmd__counter {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.bmd__counter--over {
  color: rgb(var(--v-theme-error, 211, 47, 47));
  font-weight: 600;
}

.bmd__footer-actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2, 8px);
}

.bmd__file-input {
  display: none;
}
</style>
